import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ArrowLeft, PaperPlaneTilt, Users, Eye, CheckCircle,
  Clock, Warning, Trophy, ArrowClockwise, CaretDown, CaretRight,
  Plus, Trash, XCircle, CalendarBlank, LinkSimple, Paperclip, FilePdf,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import ContactSearch from '../ui/ContactSearch';
import { toast } from 'sonner';
import EntrepriseReponseDetailModal from './EntrepriseReponseDetailModal';
import DCEComparatifDialog from './DCEComparatifDialog';
import DceHistoriqueModal from './DceHistoriqueModal';
import ConsultationQuestionsMoe from './ConsultationQuestionsMoe';
import { safeErrorMessage, logClientError } from '../../utils/errors';
import { usePermissions } from '../../hooks/usePermissions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_CONFIG = {
  EN_ATTENTE: { label: 'En attente', color: 'text-[#6B6B6B]', bg: 'bg-[#F5F5F3]', icon: Clock },
  VU: { label: 'Vu', color: 'text-[#1E3A5F]', bg: 'bg-[#E3F2FD]', icon: Eye },
  REPONDU: { label: 'Répondu', color: 'text-[#2D6A4F]', bg: 'bg-[#E8F5E9]', icon: CheckCircle },
  REFUSE: { label: 'Refusé', color: 'text-[#8B3A3A]', bg: 'bg-[#FFEBEE]', icon: Warning },
};

// A6 — dates au format français ; « Lot 03 » = place du lot dans la DPGF (P2#16)
const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString('fr-FR'); } catch { return d; }
};
const libLot = (numero, nom) => {
  const n = String(numero || '');
  const pre = /^\d+$/.test(n) ? `Lot ${n}` : n;
  return pre ? `${pre} — ${nom || ''}` : (nom || '');
};
const aujourdhui = () => new Date().toISOString().slice(0, 10);
const VIDE_INVITATION = { entreprise_nom: '', entreprise_email: '', lots_concernes: [] };

const DCE_STATUT_CONFIG = {
  EN_PREPARATION: { label: 'En préparation', color: 'text-[#6B6B6B]', bg: 'bg-[#F5F5F3]' },
  EN_COURS: { label: 'Consultation en cours', color: 'text-[#1E3A5F]', bg: 'bg-[#E3F2FD]' },
  CLOTUREE: { label: 'Clôturée', color: 'text-[#B8860B]', bg: 'bg-[#FFF8E1]' },
  CLOTURE: { label: 'Clôturée', color: 'text-[#B8860B]', bg: 'bg-[#FFF8E1]' },
  ATTRIBUEE: { label: 'Attribuée', color: 'text-[#2D6A4F]', bg: 'bg-[#E8F5E9]' },
  ANNULE: { label: 'Annulé', color: 'text-[#C53030]', bg: 'bg-[#FEF2F2]' },
};

export default function DCEManager({ chantierId, chantierName, onBack }) {
  // RP34 / D2 — le collaborateur prépare et relance ; verrouiller la DPGF, lancer la
  // consultation et inviter restent à l'architecte et au chef de projet.
  const { perms, role_projet: roleProjet } = usePermissions(chantierId);
  // RP36 — droit « Lancer et attribuer une consultation » (délégable au niveau du cabinet)
  const peutLancer = !roleProjet || !!perms.can_manage_consultation;
  const peutVerrouiller = !roleProjet || !!perms.can_lock_dpgf;
  const [dce, setDce] = useState(null);
  const [canCreateNew, setCanCreateNew] = useState(true);
  const [historiqueCount, setHistoriqueCount] = useState(0);
  const [dpgfLotsCount, setDpgfLotsCount] = useState(0);
  const [showHistorique, setShowHistorique] = useState(false);
  const [dpgfStatus, setDpgfStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [comparatifOpen, setComparatifOpen] = useState(false);
  const [detailEnt, setDetailEnt] = useState(null); // { id, nom } pour la modale détail
  const [invitations, setInvitations] = useState([{ ...VIDE_INVITATION }]);
  // A6 (P2#1, #19) — préparer la consultation : lots, DPGF, CCTP, pièces, date limite
  const [prep, setPrep] = useState(null);
  const [piecesChoix, setPiecesChoix] = useState({});
  const [lancement, setLancement] = useState(false);
  // A6 (P2#3) — date limite modifiable ; (P2#15) e-mails non partis
  const [dateDialog, setDateDialog] = useState(false);
  const [nouvelleDate, setNouvelleDate] = useState('');
  const [prevenir, setPrevenir] = useState(true);
  const [envois, setEnvois] = useState({});
  const [lienCopie, setLienCopie] = useState(null);
  const [piecesOuvert, setPiecesOuvert] = useState(false);
  const [expandedLots, setExpandedLots] = useState({});
  const [dateLimite, setDateLimite] = useState('');
  // RP61 / ARC-A11 — invitation depuis l'annuaire filtré par corps d'état
  const [annuaireLot, setAnnuaireLot] = useState('');
  const [annuaireTous, setAnnuaireTous] = useState(false);
  const [annuaire, setAnnuaire] = useState(null); // { corps_etat, entreprises }
  const [annuaireChoix, setAnnuaireChoix] = useState({});

  const chargerAnnuaire = useCallback(async (lotId, tous) => {
    if (!dce?.id || !lotId) { setAnnuaire(null); return; }
    try {
      const { data } = await axios.get(
        `${API_URL}/api/chantiers/${chantierId}/dce/${dce.id}/annuaire?lot_id=${encodeURIComponent(lotId)}${tous ? '&tous=true' : ''}`,
        { withCredentials: true },
      );
      setAnnuaire(data);
      setAnnuaireChoix({});
    } catch (err) {
      toast.error(safeErrorMessage(err, "Erreur de chargement de l'annuaire"));
    }
  }, [chantierId, dce?.id]);

  const ajouterDepuisAnnuaire = () => {
    const choisies = (annuaire?.entreprises || []).filter((e) => annuaireChoix[e.id]);
    if (!choisies.length) { toast.error('Cochez au moins une entreprise'); return; }
    setInvitations((prev) => {
      const gardees = prev.filter((i) => i.entreprise_nom || i.entreprise_email);
      const nouvelles = choisies.map((e) => ({
        entreprise_nom: e.nom, entreprise_email: e.email || '', lots_concernes: [annuaireLot],
      }));
      return [...gardees, ...nouvelles];
    });
    setAnnuaireChoix({});
    toast.success(`${choisies.length} entreprise${choisies.length > 1 ? 's' : ''} ajoutée${choisies.length > 1 ? 's' : ''}`);
  };

  const fetchDCE = useCallback(async () => {
    try {
      const [dceRes, dpgfRes] = await Promise.all([
        axios.get(`${API_URL}/api/chantiers/${chantierId}/dce`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/dpgf`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      // Sprint Fix UX post-annulation — nouveau shape {dce, can_create_new, historique_count}
      const payload = dceRes.data || {};
      setDce(payload.dce ?? null);
      setCanCreateNew(payload.can_create_new ?? (payload.dce === null || payload.dce === undefined));
      setHistoriqueCount(payload.historique_count ?? 0);
      setDpgfStatus(dpgfRes.data?.statut || null);
      setDpgfLotsCount((dpgfRes.data?.lots || []).length);
      const actif = payload.dce && ['EN_PREPARATION', 'EN_COURS'].includes(payload.dce.statut);
      if (!actif) {
        const { data: p } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/dce/preparation`, { withCredentials: true })
          .catch(() => ({ data: null }));
        setPrep(p);
      } else {
        setPiecesChoix(Object.fromEntries((payload.dce.pieces_dce || []).map((id) => [id, true])));
        const [{ data: p }, { data: e }] = await Promise.all([
          axios.get(`${API_URL}/api/chantiers/${chantierId}/dce/preparation`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API_URL}/api/chantiers/${chantierId}/dce/${payload.dce.id}/envois`, { withCredentials: true }).catch(() => ({ data: {} })),
        ]);
        setPrep(p);
        setEnvois(e?.envois || {});
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [chantierId]);

  useEffect(() => { fetchDCE(); }, [fetchDCE]);

  const handleVerrouillerDPGF = async () => {
    // A6 (P2#19) — le verrouillage fige la DPGF : confirmation explicite
    if (!window.confirm('Verrouiller la DPGF ?\n\nLes prix et les quantités seront figés : c\'est cette DPGF que les entreprises chiffreront. Vous pourrez la déverrouiller tant que la consultation n\'est pas lancée.')) return;
    try {
      await axios.put(`${API_URL}/api/chantiers/${chantierId}/dpgf/verrouiller`, {}, { withCredentials: true });
      toast.success('DPGF verrouillée et validée');
      setDpgfStatus('VALIDEE');
      fetchDCE();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du verrouillage');
    }
  };

  const handleCreateDCE = async () => {
    if (!dateLimite) { toast.error('Choisissez la date limite de remise des offres'); return; }
    setLancement(true);
    try {
      const pieces_ids = Object.keys(piecesChoix).filter((k) => piecesChoix[k]);
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/dce`,
        { date_limite_reponse: dateLimite, pieces_ids, joindre_cctp: true }, { withCredentials: true });
      toast.success('Consultation prête : invitez maintenant les entreprises');
      await fetchDCE();
      setInviteOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally { setLancement(false); }
  };

  const handleChangerDate = async () => {
    if (!nouvelleDate) return;
    try {
      const { data } = await axios.put(`${API_URL}/api/chantiers/${chantierId}/dce/${dce.id}/date-limite`,
        { date_limite_reponse: nouvelleDate, prevenir }, { withCredentials: true });
      toast.success(data.message);
      setDateDialog(false);
      fetchDCE();
    } catch (err) {
      toast.error(safeErrorMessage(err, 'Date limite non modifiée'));
    }
  };

  const handleSavePieces = async () => {
    try {
      const pieces_ids = Object.keys(piecesChoix).filter((k) => piecesChoix[k]);
      const { data } = await axios.put(`${API_URL}/api/chantiers/${chantierId}/dce/${dce.id}/pieces`,
        { pieces_ids }, { withCredentials: true });
      toast.success(data.message);
      setPiecesOuvert(false);
      fetchDCE();
    } catch (err) {
      toast.error(safeErrorMessage(err, 'Pièces non enregistrées'));
    }
  };

  // A6 (P2#15) — e-mail non parti : lien à transmettre à l'entreprise
  const copierLien = async (inv) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dce/${dce.id}/invitations/${inv.id}/lien`, {}, { withCredentials: true });
      setLienCopie({ ...data, entreprise_nom: inv.entreprise_nom });
      try { await navigator.clipboard.writeText(data.url); toast.success('Lien copié'); } catch { /* affiché dans la fenêtre */ }
    } catch (err) {
      toast.error(safeErrorMessage(err, 'Lien non disponible'));
    }
  };

  const handleInvite = async (forceDuplicates = false) => {
    // Iter 87 — A.1 garde-fou : si appelé avec un SyntheticEvent (oubli de wrapper
    // dans un onClick), forceDuplicates serait un objet React → on le neutralise.
    if (typeof forceDuplicates !== 'boolean') {
      forceDuplicates = false;
    }
    const valid = invitations.filter(i => i.entreprise_email && i.entreprise_nom && i.lots_concernes.length > 0);
    if (valid.length === 0) {
      toast.error('Renseignez au moins une invitation complète');
      return;
    }
    try {
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dce/${dce.id}/inviter`,
        { invitations: valid, allow_duplicate_lots: forceDuplicates },
        { withCredentials: true }
      );
      toast.success(data.message);
      // Sprint A — afficher warning informatif si lots différents avec entreprise déjà invitée
      if (data.duplicates_warnings?.length) {
        const summary = data.duplicates_warnings
          .map(w => `${w.entreprise_nom} a maintenant ${w.existing_invitations.length + 1} invitations sur ce DCE`)
          .join(' · ');
        toast.warning(`Multi-invitations : ${summary}`, { duration: 8000 });
      }
      setInviteOpen(false);
      setInvitations([{ ...VIDE_INVITATION }]);
      fetchDCE();
    } catch (err) {
      logClientError('DCEManager.handleInvite', err);
      // Sprint A Bug #7 — 409 doublon strict : confirmer pour forcer
      if (err.response?.status === 409 && !forceDuplicates) {
        const detail = safeErrorMessage(err, 'Doublon détecté');
        if (window.confirm(`${detail}\n\nForcer l'envoi quand même ?`)) {
          return handleInvite(true);
        }
        return;
      }
      toast.error(safeErrorMessage(err, 'Erreur envoi invitations'));
    }
  };

  const handleRelance = async (invId) => {
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/dce/${dce.id}/relancer/${invId}`, {}, { withCredentials: true });
      toast.success('Relance envoyée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur relance');
    }
  };

  const handleOpenComparatif = () => setComparatifOpen(true);

  const toggleLot = (lotId) => {
    setExpandedLots(prev => ({ ...prev, [lotId]: !prev[lotId] }));
  };

  const updateInvitation = (idx, field, value) => {
    setInvitations(prev => prev.map((inv, i) => i === idx ? { ...inv, [field]: value } : inv));
  };

  const toggleLotInInvitation = (idx, lotId) => {
    setInvitations(prev => prev.map((inv, i) => {
      if (i !== idx) return inv;
      const lots = inv.lots_concernes.includes(lotId)
        ? inv.lots_concernes.filter(l => l !== lotId)
        : [...inv.lots_concernes, lotId];
      return { ...inv, lots_concernes: lots };
    }));
  };

  if (loading) return <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;

  const dceStatut = dce ? DCE_STATUT_CONFIG[dce.statut] || DCE_STATUT_CONFIG.EN_PREPARATION : null;
  const lots = dce?.dpgf_snapshot || [];
  const respondedCount = dce?.invitations?.filter(i => i.statut === 'REPONDU').length || 0;
  const totalInvitations = dce?.invitations?.length || 0;
  const dceTermine = dce && ['ANNULE', 'CLOTURE', 'CLOTUREE', 'ATTRIBUEE'].includes(dce.statut);
  const showCreateNew = (!dce || dceTermine) && canCreateNew;
  const numeros = dce?.numeros_lots || {};
  const dateLimiteDce = dce?.date_limite_reponse;
  const documents = prep?.documents || [];

  // A6 (P2#19) — « Préparer la consultation » : lots ✓, DPGF verrouillée ✓, CCTP ✓, pièces, date limite
  const Coche = ({ ok }) => (ok
    ? <CheckCircle size={18} weight="fill" className="text-[#2D6A4F] flex-shrink-0" />
    : <XCircle size={18} className="text-[#B8860B] flex-shrink-0" />);
  const preparationBlock = (
    <div className="border border-[#E0E0DB] bg-white p-5 space-y-4" data-testid="dce-preparation">
      <div>
        <h3 className="text-lg font-semibold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
          Préparer la consultation
        </h3>
        <p className="text-xs text-[#6B6B6B]">Lots → DPGF → CCTP → pièces → date limite, puis « Lancer la consultation ».</p>
      </div>
      <ul className="space-y-2">
        {(prep?.etapes || []).map((e) => (
          <li key={e.cle} className="flex items-start gap-2 text-sm" data-testid={`dce-prep-${e.cle}`}>
            <Coche ok={e.ok} />
            <span className="flex-1">
              <span className="font-medium text-[#2C2C2C]">{e.libelle}</span>
              <span className="text-xs text-[#6B6B6B]"> — {e.detail}</span>
            </span>
            {e.cle === 'dpgf' && !e.ok && (peutVerrouiller ? (
              <Button size="sm" onClick={handleVerrouillerDPGF} className="bg-[#1E3A5F] hover:bg-[#2C4F7A] rounded-none gap-1 h-7 text-xs" data-testid="dce-verrouiller-dpgf">
                <CheckCircle size={12} weight="bold" /> Verrouiller la DPGF
              </Button>
            ) : (
              <span className="text-xs text-[#6B6B6B]" data-testid="dce-reserve-architecte">Réservé à l'architecte ou au chef de projet.</span>
            ))}
          </li>
        ))}
      </ul>
      {(prep?.lots || []).length > 0 && (
        <p className="text-xs text-[#6B6B6B]" data-testid="dce-prep-lots">
          {(prep.lots || []).map((l) => `${libLot(l.numero, l.nom)}${l.cctp_redige ? '' : ' (CCTP à rédiger)'}`).join(' · ')}
        </p>
      )}
      <div className="border-t border-[#F0F0EE] pt-3">
        <p className="text-sm font-medium text-[#2C2C2C] flex items-center gap-2 mb-1"><Paperclip size={14} /> Pièces jointes au dossier</p>
        <p className="text-xs text-[#6B6B6B] mb-2">
          Le CCTP rédigé (PDF, lot par lot) et le cadre de réponse DPGF sont joints automatiquement.
          Cochez les autres pièces à envoyer (plans, règlement de consultation…).
        </p>
        {documents.length === 0 ? (
          <p className="text-xs text-[#9A9A9A]">Aucun document dans le chantier : ajoutez vos plans dans l'onglet Documents.</p>
        ) : (
          <div className="max-h-40 overflow-y-auto border border-[#E0E0DB] divide-y divide-[#F0F0EE]">
            {documents.map((doc) => (
              <label key={doc.id} className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-[#FAFAF8]" data-testid={`dce-piece-${doc.id}`}>
                <input type="checkbox" checked={!!piecesChoix[doc.id]}
                  onChange={(ev) => setPiecesChoix((prev) => ({ ...prev, [doc.id]: ev.target.checked }))} />
                <span className="flex-1 truncate">{doc.nom}</span>
                {doc.type && <span className="text-[10px] uppercase text-[#9A9A9A]">{doc.type}</span>}
              </label>
            ))}
          </div>
        )}
      </div>
      <div className="border-t border-[#F0F0EE] pt-3 flex flex-wrap items-center gap-3">
        <Label className="text-sm text-[#2C2C2C] flex items-center gap-2"><CalendarBlank size={14} /> Date limite de remise des offres</Label>
        <Input type="date" min={aujourdhui()} value={dateLimite} onChange={e => setDateLimite(e.target.value)}
          className="rounded-none w-48" data-testid="dce-date-limite-input" />
        <span className="text-xs text-[#6B6B6B]">La même pour toutes les entreprises.</span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {peutLancer ? (
          <Button onClick={handleCreateDCE}
            disabled={lancement || !prep?.dpgf_verrouillee || dpgfLotsCount === 0 || !dateLimite}
            className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2" data-testid="dce-lancer-consultation">
            <PaperPlaneTilt size={16} weight="bold" /> Lancer la consultation
          </Button>
        ) : (
          <p className="text-xs text-[#6B6B6B]" data-testid="dce-reserve-architecte">
            Réservé à l'architecte ou au chef de projet. Demandez à l'architecte du projet.
          </p>
        )}
        {dpgfLotsCount === 0 && (
          <p className="text-xs text-[#C53030]">La DPGF est vide : ajoutez des lots dans Conception → DPGF.</p>
        )}
        {!prep?.dpgf_verrouillee && dpgfLotsCount > 0 && (
          <p className="text-xs text-[#6B6B6B]">Verrouillez d'abord la DPGF.</p>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="dce-manager">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={onBack} className="rounded-none gap-2" data-testid="dce-back-button">
          <ArrowLeft size={18} />
          Retour
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            {chantierName ? `Consultation — ${chantierName}` : 'Consultation des entreprises'}
          </h2>
          <p className="text-sm text-[#6B6B6B]">Dossier de consultation (DCE) : DPGF, CCTP et pièces</p>
        </div>
        {dceStatut && (
          <span className={`text-xs px-3 py-1 font-bold uppercase tracking-wider ${dceStatut.bg} ${dceStatut.color}`} data-testid="dce-statut-badge">
            {dceStatut.label}
          </span>
        )}
      </div>

      {/* Step 0bis: DCE terminé (ANNULE/CLOTURE/ATTRIBUEE) → bandeau info + CTA création */}
      {dceTermine && canCreateNew && (
        <div className="space-y-4 mb-6" data-testid="dce-termine-banner">
          <div className="border border-[#E0E0DB] bg-[#F8F7F4] p-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 ${dceStatut.bg} ${dceStatut.color}`}>
                Dernier DCE : {dceStatut.label}
              </span>
              {dce.statut === 'ANNULE' && dce.annule_at && (
                <span className="text-xs text-[#9A9A9A]">
                  annulé le {new Date(dce.annule_at).toLocaleDateString('fr-FR')}
                </span>
              )}
            </div>
            {dce.statut === 'ANNULE' && dce.annule_motif && (
              <p className="text-sm text-[#6B6B6B] italic mb-2" data-testid="dce-annule-motif">
                « {dce.annule_motif} »
              </p>
            )}
            <p className="text-sm text-[#2C2C2C]">
              {totalInvitations} entreprise{totalInvitations > 1 ? 's' : ''} invitée{totalInvitations > 1 ? 's' : ''}
              {' '}· {lots.length} lots dans le snapshot
            </p>
            {/* CSL-07 — le comparatif (rapport pour le MOA) reste consultable une fois le DCE terminé */}
            {respondedCount > 0 && (
              <Button
                onClick={handleOpenComparatif}
                variant="outline"
                size="sm"
                className="rounded-none gap-2 border-[#1E3A5F] text-[#1E3A5F] mt-3 mr-3"
                data-testid="dce-termine-comparatif-button"
              >
                <Trophy size={14} />
                Consulter le comparatif des offres
              </Button>
            )}
            {historiqueCount > 0 && (
              <button
                onClick={() => setShowHistorique(true)}
                className="text-xs text-[#1E3A5F] hover:underline mt-2"
                data-testid="btn-show-historique"
              >
                Voir l'historique des DCE ({historiqueCount})
              </button>
            )}
          </div>

          {/* B6 — questions / réponses de la consultation terminée (historique) */}
          <ConsultationQuestionsMoe chantierId={chantierId} dceId={dce.id} />

          <div data-testid="dce-create-new-cta">
            <p className="text-sm text-[#2C2C2C] mb-2">
              Nouvelle consultation : la DPGF actuelle contient <strong>{dpgfLotsCount} lot{dpgfLotsCount > 1 ? 's' : ''}</strong>.
            </p>
            {preparationBlock}
          </div>

          {showHistorique && (
            <DceHistoriqueModal chantierId={chantierId} onClose={() => setShowHistorique(false)} />
          )}
        </div>
      )}

      {/* Step 1: pas encore de consultation → la préparer (A6, P2#19) */}
      {!dce && preparationBlock}

      {/* Step 2: DCE active (visible si DCE existe ET pas terminé) */}
      {dce && !dceTermine && (
        <div className="space-y-6">
          {/* A6 (P2#2, #3) — une seule date limite, visible et modifiable */}
          <div className="flex flex-wrap items-center gap-3 border border-[#E0E0DB] bg-[#FAFAF8] px-4 py-3" data-testid="dce-date-limite">
            <CalendarBlank size={18} className="text-[#1E3A5F]" />
            <span className="text-sm text-[#2C2C2C] flex-1">
              Date limite de remise des offres : <strong>{dateLimiteDce ? fmtDate(dateLimiteDce) : 'non fixée'}</strong>
              <span className="text-xs text-[#6B6B6B]"> — la même pour toutes les entreprises</span>
            </span>
            {peutLancer && (
              <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs"
                onClick={() => { setNouvelleDate(dateLimiteDce || ''); setPrevenir(true); setDateDialog(true); }}
                data-testid="dce-modifier-date">
                Modifier la date limite
              </Button>
            )}
          </div>

          {/* A6 (P2#1) — pièces du dossier */}
          <div className="border border-[#E0E0DB] bg-white px-4 py-3 text-sm" data-testid="dce-pieces">
            <div className="flex flex-wrap items-center gap-2">
              <Paperclip size={14} className="text-[#1E3A5F]" />
              <span className="flex-1">
                Pièces envoyées : <FilePdf size={12} className="inline" /> CCTP (PDF par lot){dce.cctp_joint ? '' : ' actuel'},
                cadre DPGF (Excel){(dce.pieces_dce || []).length > 0 && `, ${(dce.pieces_dce || []).length} pièce${(dce.pieces_dce || []).length > 1 ? 's' : ''} jointe${(dce.pieces_dce || []).length > 1 ? 's' : ''}`}
              </span>
              {peutLancer && (
                <Button size="sm" variant="ghost" className="rounded-none text-xs" onClick={() => setPiecesOuvert((o) => !o)} data-testid="dce-modifier-pieces">
                  {piecesOuvert ? 'Fermer' : 'Modifier les pièces'}
                </Button>
              )}
            </div>
            {piecesOuvert && (
              <div className="mt-2 space-y-2">
                {documents.length === 0 ? (
                  <p className="text-xs text-[#9A9A9A]">Aucun document dans le chantier.</p>
                ) : (
                  <div className="max-h-40 overflow-y-auto border border-[#E0E0DB] divide-y divide-[#F0F0EE]">
                    {documents.map((doc) => (
                      <label key={doc.id} className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer">
                        <input type="checkbox" checked={!!piecesChoix[doc.id]}
                          onChange={(ev) => setPiecesChoix((prev) => ({ ...prev, [doc.id]: ev.target.checked }))} />
                        <span className="flex-1 truncate">{doc.nom}</span>
                      </label>
                    ))}
                  </div>
                )}
                <Button size="sm" className="rounded-none bg-[#1E3A5F] hover:bg-[#2C4F7A]" onClick={handleSavePieces}>Enregistrer les pièces</Button>
              </div>
            )}
          </div>

          {/* Summary bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border border-[#E0E0DB] bg-white p-4">
              <p className="text-2xl font-bold text-[#1E3A5F]">{lots.length}</p>
              <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Lots</p>
            </div>
            <div className="border border-[#E0E0DB] bg-white p-4">
              <p className="text-2xl font-bold text-[#D4A84B]">{totalInvitations}</p>
              <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Entreprises invitées</p>
            </div>
            <div className="border border-[#E0E0DB] bg-white p-4">
              <p className="text-2xl font-bold text-[#2D6A4F]">{respondedCount}</p>
              <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Offres reçues</p>
            </div>
            <div className="border border-[#E0E0DB] bg-white p-4">
              <p className="text-2xl font-bold text-[#C75B39]">{Object.keys(dce.attributions || {}).length}</p>
              <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Lots attribués</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {peutLancer && (dce.statut === 'EN_PREPARATION' || dce.statut === 'EN_COURS') && (
              <Button onClick={() => setInviteOpen(true)} className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2" data-testid="dce-inviter-button">
                <Users size={16} weight="bold" />
                Inviter des entreprises
              </Button>
            )}
            {respondedCount > 0 && (
              <Button onClick={handleOpenComparatif} variant="outline" className="rounded-none gap-2 border-[#1E3A5F] text-[#1E3A5F]" data-testid="dce-comparatif-button">
                <Trophy size={16} />
                Comparatif des offres
              </Button>
            )}
          </div>

          {/* Lots + Invitations */}
          <div className="card-kroquis">
            <h3 className="text-base font-semibold text-[#2C2C2C] mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Lots de la consultation
            </h3>
            <div className="space-y-2">
              {lots.map(lot => {
                const isExpanded = expandedLots[lot.id];
                const attribution = dce.attributions?.[lot.id];
                return (
                  <div key={lot.id} className="border border-[#E0E0DB]">
                    <div
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAF9]"
                      onClick={() => toggleLot(lot.id)}
                      data-testid={`dce-lot-${lot.id}`}
                    >
                      {isExpanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
                      <span className="font-medium text-[#2C2C2C] flex-1">{libLot(numeros[lot.id], lot.nom)}</span>
                      <span className="text-xs text-[#6B6B6B]">{lot.postes?.length || 0} postes</span>
                      {attribution && (
                        <span className="text-xs px-2 py-0.5 bg-[#E8F5E9] text-[#2D6A4F] font-bold">
                          Attribué
                        </span>
                      )}
                    </div>
                    {isExpanded && (
                      <div className="border-t border-[#E0E0DB] px-4 py-3 bg-[#FAFAF9]">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-[#6B6B6B] text-xs uppercase">
                              <th className="pb-2">Désignation</th>
                              <th className="pb-2 text-center">Unité</th>
                              <th className="pb-2 text-right">Qté</th>
                              <th className="pb-2 text-right">PU HT</th>
                              <th className="pb-2 text-right">Total HT</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lot.postes?.map(p => (
                              <tr key={p.id} className="border-t border-[#E0E0DB]">
                                <td className="py-1.5">{p.designation}</td>
                                <td className="py-1.5 text-center text-[#6B6B6B]">{p.unite}</td>
                                <td className="py-1.5 text-right">{p.quantite}</td>
                                <td className="py-1.5 text-right">{(p.prix_unitaire || 0).toLocaleString('fr-FR')} €</td>
                                <td className="py-1.5 text-right font-medium">{((p.quantite || 0) * (p.prix_unitaire || 0)).toLocaleString('fr-FR')} €</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Invitations list */}
          <div className="card-kroquis">
            <h3 className="text-base font-semibold text-[#2C2C2C] mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Entreprises invitées
            </h3>
            {dce.invitations?.length === 0 ? (
              <p className="text-sm text-[#9A9A9A] text-center py-6">Aucune entreprise invitée pour le moment</p>
            ) : (
              <div className="space-y-2">
                {dce.invitations?.map(inv => {
                  const cfg = STATUT_CONFIG[inv.statut] || STATUT_CONFIG.EN_ATTENTE;
                  const Icon = cfg.icon;
                  const nonParti = envois[inv.id] === 'non_parti' && inv.statut !== 'REPONDU';
                  return (
                    <div key={inv.id} className="flex flex-wrap items-center gap-3 border border-[#E0E0DB] px-4 py-3" data-testid={`dce-invitation-${inv.id}`}>
                      <Icon size={18} className={cfg.color} />
                      <div className="flex-1 min-w-[160px]">
                        <p className="text-sm font-medium text-[#2C2C2C]">{inv.entreprise_nom}</p>
                        <p className="text-xs text-[#6B6B6B] break-all">{inv.entreprise_email}</p>
                        <p className="text-[11px] text-[#6B6B6B]">{(inv.lots_concernes || []).map((l) => libLot(numeros[l], (lots.find((x) => x.id === l) || {}).nom)).join(' · ')}</p>
                        {nonParti && (
                          <p className="text-[11px] text-[#C53030] font-semibold" data-testid={`dce-envoi-non-parti-${inv.id}`}>
                            E-mail non parti : transmettez le lien à l'entreprise.
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-0.5 font-bold ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                        {inv.hors_delai && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 font-bold bg-[#FFEBEE] text-[#8B3A3A]" title="Offre envoyée après la date limite">hors délai</span>
                        )}
                        {inv.montant_total_soumis != null && (
                          <p className="text-xs text-[#2D6A4F] mt-1 font-medium">{inv.montant_total_soumis.toLocaleString('fr-FR')} € HT</p>
                        )}
                      </div>
                      {nonParti && peutLancer && (
                        <Button variant="outline" size="sm" onClick={() => copierLien(inv)} className="rounded-none text-xs gap-1 border-[#C53030] text-[#C53030]" data-testid={`dce-copier-lien-${inv.id}`}>
                          <LinkSimple size={14} /> Copier le lien
                        </Button>
                      )}
                      {['EN_ATTENTE', 'VU'].includes(inv.statut) && (
                        <Button variant="ghost" size="sm" onClick={() => handleRelance(inv.id)} className="rounded-none text-xs gap-1" data-testid={`dce-relance-${inv.id}`}>
                          <ArrowClockwise size={14} />
                          Relancer
                        </Button>
                      )}
                      {inv.statut === 'REPONDU' && inv.entreprise_id && (
                        <Button
                          size="sm"
                          onClick={() => setDetailEnt({ id: inv.entreprise_id, nom: inv.entreprise_nom, invitation_id: inv.id })}
                          className="rounded-none text-xs gap-1 bg-[#1E3A5F] hover:bg-[#152c46] text-white"
                          data-testid={`dce-voir-offre-${inv.id}`}
                        >
                          <Eye size={14} /> Voir l'offre
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* B6 — questions des entreprises : une réponse, diffusée à toutes */}
          <ConsultationQuestionsMoe chantierId={chantierId} dceId={dce.id} />
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Inviter des entreprises</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* A6 (P2#2) — une seule date limite, celle de la consultation */}
            <p className="text-sm text-[#2C2C2C] bg-[#F5F8FC] border border-[#1E3A5F]/20 px-3 py-2" data-testid="invite-date-limite">
              Date limite de remise des offres : <strong>{dateLimiteDce ? fmtDate(dateLimiteDce) : 'non fixée'}</strong> (la même pour toutes les entreprises).
            </p>
            {/* RP61 / ARC-A11 — choisir dans l'annuaire, filtré par le corps d'état du lot */}
            <div className="border border-[#1E3A5F]/30 bg-[#F5F8FC] p-3 space-y-2" data-testid="invite-annuaire">
              <p className="text-sm font-semibold text-[#1E3A5F]">Choisir dans l'annuaire</p>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={annuaireLot}
                  onChange={(e) => { setAnnuaireLot(e.target.value); chargerAnnuaire(e.target.value, annuaireTous); }}
                  className="border border-[#E0E0DB] px-2 py-1 text-sm bg-white"
                  data-testid="invite-annuaire-lot"
                >
                  <option value="">— Lot à consulter —</option>
                  {lots.map((l) => <option key={l.id} value={l.id}>{libLot(numeros[l.id], l.nom)}</option>)}
                </select>
                <label className="text-xs text-[#6B6B6B] flex items-center gap-1">
                  <input type="checkbox" checked={annuaireTous}
                    onChange={(e) => { setAnnuaireTous(e.target.checked); chargerAnnuaire(annuaireLot, e.target.checked); }} />
                  Afficher tout l'annuaire
                </label>
              </div>
              {annuaire && (
                <>
                  <p className="text-[11px] text-[#6B6B6B]">
                    {annuaire.corps_etat ? `Corps d'état du lot : ${annuaire.corps_etat}` : "Corps d'état non renseigné"}
                    {' '}· {annuaire.entreprises.length} entreprise{annuaire.entreprises.length > 1 ? 's' : ''}
                  </p>
                  {annuaire.entreprises.length === 0 ? (
                    <p className="text-xs text-[#9A9A9A]">Aucune entreprise de ce corps d'état dans l'annuaire.</p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto border border-[#E0E0DB] bg-white divide-y divide-[#F0F0EE]">
                      {annuaire.entreprises.map((e) => (
                        <label key={e.id} className={`flex items-center gap-2 px-2 py-1.5 text-sm ${e.deja_invitee ? 'opacity-50' : 'cursor-pointer hover:bg-[#FAFAF8]'}`}
                          data-testid={`invite-annuaire-ent-${e.id}`}>
                          <input type="checkbox" disabled={e.deja_invitee || !e.email}
                            checked={!!annuaireChoix[e.id]}
                            onChange={(ev) => setAnnuaireChoix((prev) => ({ ...prev, [e.id]: ev.target.checked }))} />
                          <span className="flex-1">{e.nom}
                            <span className="text-[10px] text-[#9A9A9A] ml-1">{(e.corps_etat || []).join(', ')}</span>
                          </span>
                          {e.deja_invitee && <span className="text-[10px] text-[#2D6A4F]">Déjà invitée</span>}
                          {!e.email && <span className="text-[10px] text-[#C53030]">Sans e-mail</span>}
                        </label>
                      ))}
                    </div>
                  )}
                  <Button type="button" size="sm" variant="outline" className="rounded-none gap-1"
                    onClick={ajouterDepuisAnnuaire} data-testid="invite-annuaire-ajouter">
                    <Plus size={12} /> Ajouter les entreprises cochées
                  </Button>
                </>
              )}
            </div>
            {invitations.map((inv, idx) => (
              <div key={idx} className="border border-[#E0E0DB] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#2C2C2C]">Entreprise {idx + 1}</span>
                  {invitations.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => setInvitations(prev => prev.filter((_, i) => i !== idx))} className="text-[#8B3A3A]">
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
                <div className="mb-3">
                  <Label className="text-xs text-[#6B6B6B] mb-1 block">Rechercher une entreprise</Label>
                  <ContactSearch
                    sources={['entreprises', 'users']}
                    userRoleFilter="entreprise"
                    placeholder="Tapez au moins 2 caractères…"
                    testId={`invite-search-${idx}`}
                    onSelect={(c) => {
                      updateInvitation(idx, 'entreprise_nom', c.name || c.nom || '');
                      updateInvitation(idx, 'entreprise_email', c.email || '');
                    }}
                    onCreateNew={(q) => updateInvitation(idx, 'entreprise_nom', q)}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#6B6B6B]">Nom</Label>
                    <Input value={inv.entreprise_nom} onChange={e => updateInvitation(idx, 'entreprise_nom', e.target.value)} placeholder="Nom de l'entreprise" className="rounded-none mt-1" data-testid={`invite-nom-${idx}`} />
                  </div>
                  <div>
                    <Label className="text-xs text-[#6B6B6B]">Email</Label>
                    <Input value={inv.entreprise_email} onChange={e => updateInvitation(idx, 'entreprise_email', e.target.value)} placeholder="email@entreprise.fr" className="rounded-none mt-1" data-testid={`invite-email-${idx}`} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B6B] mb-2 block">Lots concernés</Label>
                  <div className="flex flex-wrap gap-2">
                    {lots.map(lot => (
                      <button
                        key={lot.id}
                        type="button"
                        onClick={() => toggleLotInInvitation(idx, lot.id)}
                        className={`text-xs px-3 py-1.5 border transition-colors ${
                          inv.lots_concernes.includes(lot.id)
                            ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]'
                            : 'bg-white text-[#2C2C2C] border-[#E0E0DB] hover:border-[#1E3A5F]'
                        }`}
                        data-testid={`invite-lot-${idx}-${lot.id}`}
                      >
                        {libLot(numeros[lot.id], lot.nom)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              className="rounded-none gap-2 w-full"
              onClick={() => setInvitations(prev => [...prev, { ...VIDE_INVITATION }])}
              data-testid="invite-add-row"
            >
              <Plus size={14} />
              Ajouter une entreprise
            </Button>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setInviteOpen(false)} className="rounded-none">Annuler</Button>
              <Button onClick={() => handleInvite()} className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2" data-testid="invite-send-button">
                <PaperPlaneTilt size={16} />
                Envoyer les invitations
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* A6 (P2#3) — modifier la date limite, en prévenant les entreprises */}
      <Dialog open={dateDialog} onOpenChange={setDateDialog}>
        <DialogContent className="max-w-md" data-testid="dce-date-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Modifier la date limite</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="date" min={aujourdhui()} value={nouvelleDate} onChange={(e) => setNouvelleDate(e.target.value)}
              className="rounded-none w-48" data-testid="dce-nouvelle-date" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={prevenir} onChange={(e) => setPrevenir(e.target.checked)} />
              Prévenir les entreprises invitées (e-mail et notification)
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-none" onClick={() => setDateDialog(false)}>Annuler</Button>
              <Button className="rounded-none bg-[#1E3A5F] hover:bg-[#2C4F7A]" disabled={!nouvelleDate}
                onClick={handleChangerDate} data-testid="dce-date-enregistrer">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* A6 (P2#15) — lien à transmettre quand l'e-mail n'est pas parti */}
      <Dialog open={!!lienCopie} onOpenChange={(o) => { if (!o) setLienCopie(null); }}>
        <DialogContent className="max-w-lg" data-testid="dce-lien-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Lien pour {lienCopie?.entreprise_nom}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B6B6B]">
            {lienCopie?.type === 'activation'
              ? "L'e-mail n'est pas parti. Transmettez ce lien à l'entreprise : elle choisira son mot de passe et arrivera sur l'appel d'offres."
              : "L'e-mail n'est pas parti. Transmettez ce lien à l'entreprise pour ouvrir ses appels d'offres."}
          </p>
          <Input readOnly value={lienCopie?.url || ''} className="rounded-none text-xs" onFocus={(e) => e.target.select()} />
          <div className="flex justify-end gap-2">
            <Button className="rounded-none" onClick={async () => {
              try { await navigator.clipboard.writeText(lienCopie?.url || ''); toast.success('Lien copié'); } catch { toast.info('Sélectionnez le lien et copiez-le'); }
            }}>Copier le lien</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Comparatif (DCE courant) — composant partagé avec l'historique et le hub Marchés (CSL-04/07) */}
      <DCEComparatifDialog
        chantierId={chantierId}
        dceId={dce?.id}
        dceStatut={dce?.statut}
        open={comparatifOpen}
        onOpenChange={setComparatifOpen}
        onAttribue={fetchDCE}
      />

      {/* Modale de détail de la réponse d'une entreprise */}
      {detailEnt && dce?.id && (
        <EntrepriseReponseDetailModal
          chantierId={chantierId}
          dceId={dce.id}
          entrepriseId={detailEnt.id}
          entrepriseNom={detailEnt.nom}
          invitationId={detailEnt.invitation_id}
          onClose={() => setDetailEnt(null)}
        />
      )}
    </div>
  );
}
